import React, {
    createContext,
    useReducer,
    useContext,
    ReactNode,
    Dispatch,
    Context,
    useMemo,
} from "react";

type FormField = {
    name: string;
    value: unknown;
};

type FormFields = {
    [key: string]: FormField;
};

export type State = {
    fields: FormFields;
};

type Action =
  | { type: "SET_FIELD"; payload: FormField };

const initialState: State = {
    fields: {},
};

function formReducer(state: State, action: Action): State {
    switch (action.type) {
        case "SET_FIELD":
            return {
                ...state,
                fields: {
                    ...state.fields,
                    [action.payload.name]: action.payload,
                },
            };
        default:
            // we have to cast the action.type to a string because the action type can never hit here
            // this would only occur if someone is playing around where they shouldn't be from the frontend
            console.warn(`Unhandled action type: ${action.type as string}`);
            return state;
    }
}

type FormContextType = {
    state: State;
    dispatch: Dispatch<Action>;
};

// Ensure both the host bundle and the pre-bundled visual-editor runtime share
// the exact same React context instance, even if the module is evaluated twice.
const CONTEXT_KEY = '__user_form_context__';
const globalObject = (typeof globalThis !== 'undefined' ? globalThis : window) as Record<string, unknown>;
const existingContext = globalObject[CONTEXT_KEY] as Context<FormContextType | undefined> | undefined;

export const FormContext = existingContext ?? createContext<FormContextType | undefined>(undefined);

if (!existingContext) {
    globalObject[CONTEXT_KEY] = FormContext;
}

type FormProviderProps = {
    children: ReactNode;
};

export function FormProvider({ children }: FormProviderProps) {
    const [state, dispatch] = useReducer(formReducer, initialState);

    return (
        <FormContext.Provider value={{ state, dispatch }}>
            {children}
        </FormContext.Provider>
    );
}

export function useFormContext() {
    const context = useContext(FormContext);

    if (!context) {
        throw new Error("useFormContext must be used within a FormProvider");
    }

    return context;
}
